import React from 'react';
import {ApiRequestParams, PaginatedResult} from '../api/types';
import useApiClient from './useApiClient';

interface Props<T> {
  data: T[];
  setData: (t: T[]) => void;
  page: number;
  setPage: (p: number) => void;
  endReached: boolean;
  setEndReached: (e: boolean) => void;
  resetAll: () => void;
  load: (apiRequest: ApiRequestParams, reset: boolean) => void;
  loading: boolean;
}

function usePagination<T>(): Props<T> {
  const [data, setData] = React.useState<T[]>([]);
  const [page, setPage] = React.useState(1);
  const [endReached, setEndReached] = React.useState(false);

  const {fetch, loading} = useApiClient();

  const resetAll = React.useCallback(() => {
    setPage(1);
    setData([]);
  }, []);

  const load = React.useCallback(
    (apiRequest: ApiRequestParams, reset) => {
      const params = apiRequest.params
        ? {...apiRequest.params, page: reset ? 1 : page}
        : {page: reset ? 1 : page};
      fetch({...apiRequest, params})
        .then((response: PaginatedResult<T[]>) => {
          if (reset) {
            setData(response.results);
            setPage(2);
          } else {
            setData(data.concat(response.results));
            setPage(page + 1);
          }
          if (!response.next) {
            setEndReached(true);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [fetch, page, data],
  );

  return {
    data,
    setData,
    page,
    setPage,
    endReached,
    setEndReached,
    resetAll,
    load,
    loading,
  };
}

export default usePagination;
